import { Divider } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Widgets
import Subjects from '../widgets/subjects';
import Progress from '../widgets/progress';
import Tasks from '../widgets/tasks';
import Recommendations from '../widgets/recommendations';
import StakeholdersDistribution from '../widgets/stakeholders-distribution';
import TradeRelationships from '../widgets/trade-relationships';
import Map from '../widgets/map';

import RecordedEvents from '../sections/recorded-events';
import useFetchHelper from '../../../../../../helpers/useFetchHelper';

const Layout = ({
  data = {},
  selectedProject = null,
  t = (t) => t,
  language,
  projectData = null,
  user = null,
  organisationName = null,
  sourceId = null,
  isOwnData = true,
}) => {
  const mapRef = useRef();
  const stakeholdersRef = useRef();
  const recsRef = useRef();
  const [siderHeight, setSiderHeight] = useState(0);
  const ref = useRef(null);

  const {
    canFetch: showMap,
  } = useFetchHelper({ ref: mapRef });

  const {
    canFetch: showRecs,
  } = useFetchHelper({ ref: recsRef });

  const {
    canFetch: showStakeholders,
  } = useFetchHelper({ ref: stakeholdersRef });

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0].contentRect.height) {
        setSiderHeight(entries[0].contentRect.height);
      }
    })
    if (ref) {
      resizeObserver.observe(ref.current);
    }
  }, [ref]);

  const sourceAndLanguage = useMemo(() => ({
    sourceId,
    language,
  }), [sourceId, language]);

  const sourceAndProject = useMemo(() => ({
    sourceId,
  }), [sourceId]);

  const mapData = useMemo(() => {
    return data.map ? data.map.filter((d) => (
      (d.marker.lat > -85 && d.marker.lat < 85) &&
      (d.marker.lng > -180 && d.marker.lng < 180)
    )) : null;
  }, [data.map]);

  return (
    <>
      <Divider  orientation="left">{t('Dashboard::User Activity')}</Divider>
      <section ref={ref} className="margin-md">
        <div className="sider" style={{ maxWidth: 400 }}>
          <Subjects
            data={data.subjects}
            t={t}
          />
          <Progress
            data={data.progress}
            valToResetFilters={sourceAndProject}
            language={language}
            t={t}
          />
        </div>
        <div className="main" style={{ overflowX: 'auto', padding: 20, margin: -20 }}>
          <Tasks
            siderHeight={siderHeight}
            data={data.tasks}
            t={t}
            language={language}
            valToResetFilters={sourceAndProject}
            selectedProject={selectedProject}
            sourceId={sourceId}
          />
        </div>
      </section>
      <div className="multi-section">
        <RecordedEvents
          t={t}
          language={language}
          selectedProject={selectedProject}
          incidentsTrend={data.incidentsTrend}
          mitigationActionsTrend={data.mitigationActionsTrend}
          eventsDistribution={data.sdgs}
          organisationName={organisationName}
          sourceAndProject={sourceAndProject}
          options={data.mitigationActionsTrend && data.mitigationActionsTrend.options ? ({
            riskCategory: data.mitigationActionsTrend.options.riskCategory,
            riskType: data.mitigationActionsTrend.options.riskType,
          }) : null}
        />
      </div>
      <Divider orientation="left">{t('Dashboard::Scope of Monitoring')}</Divider>
      <section ref={mapRef}>
        {showMap && (
          <Map
            t={t}
            data={mapData}
            user={user}
            projectData={projectData}
            selectedProject={selectedProject}
            language={language}
            sourceId={sourceId}
          />
        )}
      </section>
      <Divider orientation="left">{t('Dashboard::Risk Management')}</Divider>
      <section ref={recsRef}>
        <Recommendations
          language={language}
          data={data.recommendations}
          allEvents={data.allEvents || []}
          selectedProject={selectedProject}
          t={t}
          organisationName={organisationName}
          isOwnData={isOwnData}
          sourceId={sourceId}
          canFetch={showRecs}
          project={selectedProject}
          user={user}
        />
      </section>
      <Divider orientation="left">{t('Dashboard::Stakeholders')}</Divider>
      <section ref={stakeholdersRef}>
        <div className="sider width-350">
          <StakeholdersDistribution
            data={data.distributionOfStakeholders}
            language={sourceAndLanguage}
            selectedProject={selectedProject}
            sourceId={sourceId}
            canFetch={showStakeholders}
            t={t}
          />
        </div>
        <div className="main">
          <TradeRelationships
            t={t}
            trade={data.trade}
            selectedProject={selectedProject}
            sourceId={sourceId}
            language={sourceAndLanguage}
            canFetch={showStakeholders}
          />
        </div>
      </section>
    </>
  );
};

export default Layout;
